import { UserRole } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { reportsConstants } from 'locale/team'
import { startCase } from 'lodash'
import styled from 'styled-components'
import EditProfileCard from '../../../../../../components/forms/screens/TeamMemberEditFormV2/EditProfileCard'
import { ProfilePicture } from '../../../../../../components/generic'
import ProfileFieldRow from './ProfileFieldRow'

const Identity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`
const IdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`
const NameGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const Name = styled.h1`
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  margin: 0;
`
const JobTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.neutral.color3};
`
const Email = styled.div`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 16px;
`
const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 243px));
  gap: 16px;
  margin-bottom: 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    grid-template-columns: repeat(2, minmax(0, 243px));
  }
`
const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`
const MetaLabel = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`
const MetaValue = styled.div`
  font-size: 16px;
  font-weight: 500;
`

const formatRoleLabel = (
  profile: Record<string, any>,
  t?: (k: string) => string
) => {
  const roleLabel = t?.(profile.role) || startCase(profile.role)
  return profile.role === UserRole.seniorCarer && profile.permissionsTemplate
    ? `${roleLabel} +`
    : roleLabel
}

type Props = {
  profile: Record<string, any>
  t: (k: string) => string
  isCurrentGroupUser?: boolean
  homesList?: Record<string, any>[]
}

const ProfileHeaderCard = ({
  profile,
  t,
  isCurrentGroupUser,
  homesList
}: Props) => {
  const noInfo = t(reportsConstants.PROFILE_VIEW_NO_INFO)

  const metaItems = [
    {
      label: t(reportsConstants.PROFILE_VIEW_ROLE),
      value: formatRoleLabel(profile, t) || noInfo
    },
    {
      label: t(reportsConstants.PROFILE_VIEW_STATUS),
      value: profile.active ? 'Active' : 'Inactive'
    },
    {
      label: t(reportsConstants.PROFILE_VIEW_CREATED_ON),
      value: profile.createdOn
        ? dayjs(profile.createdOn).format('DD-MM-YYYY')
        : noInfo
    },
    {
      label: t(reportsConstants.PROFILE_VIEW_LAST_SEEN),
      value: profile.lastSeen
        ? dayjs(profile.lastSeen).format('DD-MM-YYYY HH:mm')
        : noInfo
    }
  ]

  const getSitesValue = () => {
    if (!isCurrentGroupUser || !homesList) {
      return noInfo
    }
    const homeIds = profile?.homes?.length ? profile?.homes : [profile?.home]
    const siteNames = homeIds
      ?.map(
        (homeId: string) => homesList.find((doc) => doc.id === homeId)?.name
      )
      .filter(Boolean)
      .join(', ')
    return siteNames || noInfo
  }

  return (
    <EditProfileCard title="" testId="profile-view-header" fullWidth>
      <Identity>
        <ProfilePicture size={80} url={profile.picture} />
        <IdentityText>
          <NameGroup>
            <Name>{`${profile.name.first} ${profile.name.last}`}</Name>
            {profile.jobTitle && <JobTitle>{profile.jobTitle}</JobTitle>}
          </NameGroup>
          {profile.email && <Email>{profile.email}</Email>}
        </IdentityText>
      </Identity>
      <MetadataGrid>
        {metaItems.map((item) => (
          <MetaItem key={item.label}>
            <MetaLabel>{item.label}</MetaLabel>
            <MetaValue>{item.value}</MetaValue>
          </MetaItem>
        ))}
      </MetadataGrid>
      {isCurrentGroupUser && (
        <ProfileFieldRow
          label={t(reportsConstants.PROFILE_VIEW_SITES)}
          value={getSitesValue()}
        />
      )}
      <ProfileFieldRow
        label={t(reportsConstants.PROFILE_VIEW_NOTES)}
        value={profile.summary}
      />
    </EditProfileCard>
  )
}

export default ProfileHeaderCard
